import { useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";

const nf = new Intl.NumberFormat("sv-SE");
const kr = (n: number) => nf.format(Math.round(n)) + " kr";
const pct = (n: number) => n.toFixed(2).replace(".", ",") + " %";

function annuityPayment(amount: number, months: number, monthlyRate: number) {
  if (monthlyRate <= 0) return amount / months;
  return (amount * monthlyRate) / (1 - Math.pow(1 + monthlyRate, -months));
}

/** Betalplan exklusive aviavgift. */
function schedule(amount: number, months: number, annualRate: number, type: "annuitet" | "rak") {
  const r = annualRate / 100 / 12;
  const rows: number[] = [];
  if (type === "annuitet") {
    const pay = annuityPayment(amount, months, r);
    for (let i = 0; i < months; i++) rows.push(pay);
  } else {
    const amort = amount / months;
    let debt = amount;
    for (let i = 0; i < months; i++) {
      rows.push(amort + debt * r);
      debt -= amort;
    }
  }
  return rows;
}

/** Effektiv årsränta via bisektion på månads-IRR. */
function effectiveRate(net: number, payments: number[]) {
  if (net <= 0) return 0;
  const npv = (r: number) => payments.reduce((s, p, i) => s + p / Math.pow(1 + r, i + 1), 0) - net;
  if (npv(0) <= 0) return 0;
  let lo = 0;
  let hi = 1;
  for (let i = 0; i < 200; i++) {
    const mid = (lo + hi) / 2;
    if (npv(mid) > 0) lo = mid;
    else hi = mid;
  }
  const r = (lo + hi) / 2;
  return (Math.pow(1 + r, 12) - 1) * 100;
}

export type ArticleCalculatorConfig = {
  /** T.ex. "Räkna på lån utan UC" – ska innehålla ämnets sökord. */
  title: string;
  /** Kort intro ovanför kalkylatorn. */
  intro?: string;
  /** Kort SEO-text under kalkylen. */
  note?: string;
  amount?: number;
  minAmount?: number;
  maxAmount?: number;
  /** Löptid i månader (räknas om till år i reglaget). */
  months?: number;
  maxMonths?: number;
  rate?: number;
  minRate?: number;
  maxRate?: number;
  /** Uppläggningsavgift i kr. */
  setupFee?: number;
  /** Aviavgift per månad i kr. */
  invoiceFee?: number;
};

export function ArticleCalculator(cfg: ArticleCalculatorConfig) {
  const minAmount = cfg.minAmount ?? 1000;
  const maxAmount = cfg.maxAmount ?? 50000;
  const step = maxAmount <= 60000 ? 500 : 5000;
  const maxMonths = Math.max(6, cfg.maxMonths ?? 60);

  const [amount, setAmount] = useState(cfg.amount ?? Math.min(50000, maxAmount));
  const [months, setMonths] = useState(
    Math.min(maxMonths, Math.max(3, cfg.months ?? 12)),
  );
  const [rate, setRate] = useState(cfg.rate ?? 19.9);
  const [setupFee, setSetupFee] = useState(cfg.setupFee ?? 0);
  const [aviFee, setAviFee] = useState(cfg.invoiceFee ?? 0);
  const [type, setType] = useState<"annuitet" | "rak">("annuitet");

  

  const { first, last, total, eff } = useMemo(() => {
    const rows = schedule(amount, months, rate, type);
    const withFees = rows.map((p) => p + aviFee);
    const totalSum = withFees.reduce((a, b) => a + b, 0);
    return {
      first: withFees[0] ?? 0,
      last: withFees[withFees.length - 1] ?? 0,
      total: totalSum + setupFee,
      eff: effectiveRate(amount - setupFee, withFees),
    };
  }, [amount, months, rate, type, aviFee, setupFee]);

  return (
    <section aria-label={cfg.title} className="not-prose my-8">
      <h2 className="font-display text-xl font-extrabold tracking-tight text-foreground sm:text-2xl">
        {cfg.title}
      </h2>
      {cfg.intro && (
        <p className="mt-2 text-[14px] leading-relaxed text-foreground/80">{cfg.intro}</p>
      )}

      <div className="mt-5 grid gap-6 rounded-md border border-border bg-card p-5 sm:p-6 md:grid-cols-2">
        <div>
          <div className="font-display text-lg font-bold text-foreground">
            Lånebelopp: {kr(amount)}
          </div>
          <input
            type="range"
            min={minAmount}
            max={maxAmount}
            step={step}
            value={amount}
            aria-label="Lånebelopp i kronor"
            onChange={(e) => setAmount(Number(e.target.value))}
            className="range-slider mt-3 w-full"
            style={{ ["--val" as any]: `${((amount - minAmount) / (maxAmount - minAmount)) * 100}%` }}
          />
          <div className="mt-1 flex justify-between text-xs text-muted-foreground">
            <span>{kr(minAmount)}</span>
            <span>{kr(maxAmount)}</span>
          </div>
        </div>

        <div>
          <div className="font-display text-lg font-bold text-foreground">
            Återbetalningstid: {months} {months === 1 ? "månad" : "månader"}
            {months >= 12
              ? ` (${(months / 12).toFixed(months % 12 === 0 ? 0 : 1).replace(".", ",")} år)`
              : ""}
          </div>
          <input
            type="range"
            min={3}
            max={maxMonths}
            step={1}
            value={months}
            aria-label="Återbetalningstid i månader"
            onChange={(e) => setMonths(Number(e.target.value))}
            className="range-slider mt-3 w-full"
            style={{ ["--val" as any]: `${((months - 3) / Math.max(1, maxMonths - 3)) * 100}%` }}
          />
          <div className="mt-1 flex justify-between text-xs text-muted-foreground">
            <span>3 månader</span>
            <span>{maxMonths} månader</span>
          </div>
        </div>

        <NumberField label="Nominell ränta" suffix="%" value={rate} step={0.01} onChange={setRate} />
        <NumberField label="Avi-avgift" suffix="Kr" value={aviFee} step={1} onChange={setAviFee} />
        <NumberField
          label="Uppläggningsavgift"
          suffix="Kr"
          value={setupFee}
          step={1}
          onChange={setSetupFee}
        />

        <div>
          <div className="mb-2 font-display text-lg font-bold text-foreground">Amorteringstyp</div>
          <div className="grid grid-cols-2 gap-2 rounded-md border border-input p-1">
            {(["annuitet", "rak"] as const).map((v) => (
              <button
                key={v}
                type="button"
                onClick={() => setType(v)}
                className={`rounded-md px-3 py-2.5 text-sm font-semibold transition ${
                  type === v ? "bg-primary-soft text-primary ring-1 ring-primary" : "text-foreground hover:bg-muted"
                }`}
              >
                {v === "annuitet" ? "Annuitet" : "Rak amortering"}
              </button>
            ))}
          </div>
        </div>
      </div>

      <dl className="mt-4 grid gap-4 rounded-md bg-primary-soft p-5 sm:grid-cols-4">
        <Result
          label={type === "rak" ? "Månadskostnad (första)" : "Månadskostnad"}
          value={kr(first)}
          primary
        />
        {type === "rak" && <Result label="Månadskostnad (sista)" value={kr(last)} />}
        <Result label="Räntekostnad" value={kr(Math.max(0, total - amount))} />
        <Result label="Total kostnad" value={kr(total)} />
        {type !== "rak" && <Result label="Effektiv ränta" value={pct(eff)} />}
      </dl>

      <div className="mt-4">
        <Link
          to="/guider/lanekalkyl"
          search={{
            amount,
            months,
            rate,
            setup: setupFee,
            avi: aviFee,
            type,
          }}
          aria-label="Se mina låneförslag i lånekalkylatorn"
          className="inline-flex items-center gap-2 rounded-md bg-cta px-6 py-3 text-sm font-bold !text-cta-foreground !no-underline transition hover:brightness-105"
        >
          Se mina låneförslag <ArrowRight className="h-4 w-4" />
        </Link>
      </div>

      {cfg.note && (
        <p className="mt-4 text-[13px] leading-relaxed text-foreground/75">{cfg.note}</p>
      )}
      <p className="mt-2 text-[12px] leading-relaxed text-muted-foreground">
        Kalkylatorn är vägledande. Din faktiska månadskostnad och effektiva ränta bestäms av
        långivaren efter kreditprövning.
      </p>
    </section>
  );
}

function NumberField({
  label,
  suffix,
  value,
  step,
  onChange,
}: {
  label: string;
  suffix: string;
  value: number;
  step: number;
  onChange: (n: number) => void;
}) {
  return (
    <div>
      <div className="mb-2 font-display text-lg font-bold text-foreground">{label}</div>
      <div className="flex items-center rounded-md border border-input bg-card px-4">
        <input
          type="number"
          min={0}
          step={step}
          value={value}
          aria-label={label}
          onChange={(e) => onChange(Math.max(0, Number(e.target.value) || 0))}
          className="w-full bg-transparent py-3 text-[15px] outline-none"
        />
        <span className="pl-2 font-semibold text-foreground">{suffix}</span>
      </div>
    </div>
  );
}

function Result({ label, value, primary = false }: { label: string; value: string; primary?: boolean }) {
  return (
    <div className="text-center">
      <dt className="text-xs font-medium text-muted-foreground">{label}</dt>
      <dd
        className={`mt-1 font-display text-lg font-black ${primary ? "text-primary" : "text-foreground"}`}
      >
        {value}
      </dd>
    </div>
  );
}
